import React from 'react';
import useLocalize from '../../../utils/hooks/useLocalize';
import { getExchangeResourceId, isJpeq, isUseq } from '../../../utils/symbol';
import './SymbolInfoHeader.scss';

/**
 * SymbolInfoHeader - Header section displaying symbol information
 *
 * @param {Object} props - Component props
 * @param {Object} props.symbolMetadata - Symbol metadata object
 * @returns {React.Component} Symbol info header component
 */
const SymbolInfoHeader = React.memo(({ symbolMetadata }) => {
  const localize = useLocalize();

  const symbolName =
    symbolMetadata.name || symbolMetadata.jpShortName || symbolMetadata.root;

  const symbolRoot =
    isJpeq(symbolMetadata.assetClass) || isUseq(symbolMetadata.assetClass)
      ? symbolMetadata.root
      : '';

  // Get exchange information
  const exchangeResourceId = getExchangeResourceId(symbolMetadata);
  const exchangeText =
    exchangeResourceId !== ''
      ? localize(
          isJpeq(symbolMetadata.assetClass)
            ? `${exchangeResourceId}.long`
            : exchangeResourceId
        )
      : '';

  // Get industry information
  const industryText = symbolMetadata.jpIndustry
    ? localize(`symbol.industry.${symbolMetadata.jpIndustry}`)
    : '';

  // Get lot size information
  const lotSizeInfo = symbolMetadata.lotSize
    ? {
        label: localize('symbol.details.lotsize'),
        value: `${symbolMetadata.lotSize}${localize(
          'symbol.details.lotsize.units'
        )}`
      }
    : null;

  // Check if symbol is part of Nikkei 225
  const isNikkei225 =
    symbolMetadata.indexes && symbolMetadata.indexes.includes('Nikkei225');

  return (
    <div className="symbol-info-header">
      {/* Symbol Information Row */}
      <div className="symbol-info-row">
        <span className="symbol-root">{symbolRoot}</span>
        <span className="symbol-name">{symbolName}</span>
      </div>
      <div className="symbol-info-row">
        {exchangeText && <span className="exchange">{exchangeText}</span>}
        {isNikkei225 && (
          <span className="nikkei-flag">
            {localize('symbol.details.nikkei.flag.short')}
          </span>
        )}
        {industryText && <span className="industry">{industryText}</span>}
        {lotSizeInfo && (
          <span className="lot-size">
            <div>{lotSizeInfo.label}</div>
            <div>{lotSizeInfo.value}</div>
          </span>
        )}
      </div>
    </div>
  );
});

SymbolInfoHeader.displayName = 'SymbolInfoHeader';

export default SymbolInfoHeader;
